import { registerDestructor } from '@ember/destroyable';
import { tracked } from 'tracked-built-ins';

import { minDate, maxDate } from 'rise-ui/config/time-series/query-params.js';
import { formatDate, offsetDate, parseDate } from 'rise-ui/utils/date.js';
import {
  getResultsUrl, getExposedResultsUrl, pagingFromResponse, resultsFromResponse,
} from 'rise-ui/utils/time-series/result.js';
import NextPager from 'rise-ui/utils/least-next-pager.js';
import { join } from 'rise-ui/utils/string.js';

export class ResultLoader {
  /**
   * @name ResultLoaderQuery
   * @interface
   * @property {string} [itemId]
   * @property {string} [modelRunId]
   * @property {{from:string,to:string}} [datesRanges]
   */
  query;
  mockPages;
  mockPaging;

  pagesLoaded = tracked({});
  pagesRequested = tracked({});
  @tracked loadedItems = 0;
  @tracked requestedPages = 0;
  @tracked outstandingPages = 0;
  @tracked loadedPages = 0;
  @tracked itemsPerPage;
  @tracked totalItems;
  @tracked totalPages;
  @tracked currentPage;
  @tracked error;
  @tracked loading = false;
  @tracked complete = false;

  pager = new NextPager();
  worker;

  constructor({ rootURL, query, mockPages }) {
    this.query = query;

    if (mockPages) {
      this.mockPages = mockPages;
      this.mockPaging = {
        totalPages: mockPages.length,
        totalItems: mockPages.reduce((s, p) => s + p.length, 0),
        itemsPerPage: mockPages[0].length,
      };
    }

    if (!mockPages) {
      const worker = this.worker = new Worker(
        new URL('../workers/graph-worker.js', import.meta.url)
      );
      worker.onmessage = ({ data: { data, page, type } }) => {
        const request = this.pagesRequested[page];
        if (!request || !type) {
          return;
        }
        if (type === 'error') {
          request.reject(data);
        } else {
          request.resolve(data);
        }
      };
      worker.postMessage('init');
      registerDestructor(this, () => {
        this.worker.terminate();
      });
    }
  }

  getQueryParams(page = null) {
    const queryParams = {
      itemsPerPage: 2000,
      'order[dateTime]': 'ASC',
    };
    const { query } = this;

    if (query.modelRunId) {
      queryParams.modelRunId = query.modelRunId;
    } else if (query.itemId) {
      queryParams.itemId = query.itemId;
    } else {
      console.error('Expected item or model-run ID');
    }

    const dates = query.datesRanges;
    if (dates) {
      let date;
      if ((date = dates.from) !== minDate
        && (date = parseDate(date))
      ) {
        queryParams['dateTime[after]'] = formatDate(date, 'url');
      }
      if ((date = dates.to) !== maxDate && (date = parseDate(date))) {
        // Strictly before next day to include all time during chosen day
        queryParams['dateTime[strictly_before]']
          = formatDate(offsetDate(date, 1, 'day'), 'url');
      }
    }

    if (page && +page !== 1) {
      queryParams.page = page;
    }

    return queryParams;
  }

  getUrl(page = null) {
    return getResultsUrl(this.getQueryParams(page));
  }

  getExposedUrl(page = null) {
    return getExposedResultsUrl(this.getQueryParams(page));
  }

  next() {
    const { mockPages } = this;
    const currentPage = this.pager.next(this.pagesLoaded, this.totalPages);
    if (!currentPage && currentPage !== 0 || this.pagesRequested[currentPage]) {
      return;
    }

    this.requestedPages = this.requestedPages + 1;
    this.outstandingPages = this.outstandingPages + 1;

    if (mockPages) {
      setTimeout(() => {
        this.pagesRequested[currentPage].resolve();
      }, 2000);
    } else {
      this.worker.postMessage({
        type: 'fetch',
        url: this.getUrl(currentPage),
        page: currentPage,
      });
    }

    return new Promise((resolve, reject) => {
      this.pagesRequested[currentPage] = {
        reject: error => {
          delete this.pagesRequested[currentPage];

          this.loading = false;
          this.error = error;
          this.requestedPages = this.requestedPages - 1;
          this.outstandingPages = this.outstandingPages - 1;

          reject(error);
        },

        resolve: response => {
          delete this.pagesRequested[currentPage];

          this.pagesLoaded[currentPage] = true;
          this.loading = false;
          this.error = undefined;
          this.outstandingPages = this.outstandingPages - 1;
          this.loadedPages = this.loadedPages + 1;

          const {
            totalPages, totalItems, itemsPerPage,
          } = mockPages ? this.mockPaging : pagingFromResponse(response);
          this.totalPages = totalPages;
          this.totalItems = totalItems;
          this.itemsPerPage = itemsPerPage;

          if (!totalPages || this.loadedPages >= totalPages) {
            this.complete = true;
          }

          const results = mockPages ? mockPages[currentPage - 1]
            : resultsFromResponse(response, currentPage);
          if (results && results.length) {
            this.loadedItems = this.loadedItems + results.length;
          }

          resolve(results);
        },
      };
    });
  }

  clearError() {
    this.error = undefined;
  }
}
